import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DollarSign,
    Shield,
    TrendingUp,
    Users,
    Scroll,
    Target,
    Star,
    Lock,
} from 'lucide-react';

const features = [
    {
        icon: Scroll,
        title: 'Debt Contracts',
        description:
            'Create binding financial contracts with customizable terms, interest rates, and payment schedules.',
    },
    {
        icon: TrendingUp,
        title: 'Compound Interest',
        description:
            'Automatic interest accrual — daily, weekly, biweekly, or monthly compounding keeps debt growing.',
    },
    {
        icon: DollarSign,
        title: 'Fees & Taxes',
        description:
            'Define custom fees and taxes with triggers for missed payments, minimum-only payments, or manual assessment.',
    },
    {
        icon: Target,
        title: 'Pillory',
        description:
            'Public punishment posts where the community can vote to add fees to a sub\'s contract balance.',
    },
    {
        icon: Shield,
        title: 'Consent System',
        description:
            'Double-consent workflow for restricted contract changes. Both parties must agree before terms change.',
    },
    {
        icon: Users,
        title: 'Community',
        description:
            'Public profiles, reviews, Wall of Shame, and a live fee/tax feed to share and compare.',
    },
    {
        icon: Lock,
        title: 'Immutable Ledger',
        description:
            'Every payment, fee, tax, and interest charge is permanently recorded. No edits, no deletions.',
    },
    {
        icon: Star,
        title: 'Reviews & Ratings',
        description:
            'Rate and review doms after contracts end. Build reputation through verified experiences.',
    },
];

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-200">
            {/* Nav */}
            <nav className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
                    <h1 className="flex items-center gap-2 text-xl font-bold text-violet-400">
                        <img src="/images/monogram.png" alt="" className="h-8 w-8" />
                        DebtBound
                    </h1>
                    <div className="flex items-center gap-3">
                        <Link to="/docs">
                            <Button variant="ghost" size="sm">
                                Docs
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="ghost" size="sm">
                                Log in
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button size="sm" className="bg-violet-600 hover:bg-violet-700">
                                Sign Up
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="mx-auto max-w-5xl px-4 py-20 text-center">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-violet-800/50 bg-violet-950/30 px-4 py-1.5 text-xs text-violet-300">
                    <DollarSign className="h-3 w-3" />
                    Financial Domination Platform
                </div>
                <h2 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                    Your wallet.{' '}
                    <span className="text-violet-400">Their rules.</span>
                </h2>
                <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
                    DebtBound is a debt contract management platform for financial domination.
                    Doms create contracts with compound interest, custom fees, and punishments.
                    Subs sign and pay — with every transaction permanently recorded.
                </p>
                <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                    <Link to="/register">
                        <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-base px-8">
                            Get Started
                        </Button>
                    </Link>
                    <Link to="/login">
                        <Button variant="outline" size="lg" className="text-base px-8">
                            Log In
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Stats */}
            <section className="border-y border-slate-800/50 bg-slate-900/30">
                <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 px-4 py-12 sm:grid-cols-4">
                    <div className="text-center">
                        <p className="text-2xl font-bold text-violet-400">$$$</p>
                        <p className="mt-1 text-xs text-slate-500">Debt Managed</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-amber-400">Compound</p>
                        <p className="mt-1 text-xs text-slate-500">Interest Accrual</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-red-400">Immutable</p>
                        <p className="mt-1 text-xs text-slate-500">Ledger Records</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-green-400">Consent</p>
                        <p className="mt-1 text-xs text-slate-500">Based Changes</p>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="mx-auto max-w-5xl px-4 py-20">
                <div className="mb-12 text-center">
                    <h3 className="text-2xl font-bold sm:text-3xl">
                        Everything you need for{' '}
                        <span className="text-violet-400">financial control</span>
                    </h3>
                    <p className="mt-3 text-slate-400">
                        From contract creation to public shaming — every tool for findom.
                    </p>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {features.map((feature) => (
                        <Card key={feature.title} className="bg-slate-900/50 border-slate-800">
                            <CardContent className="p-5">
                                <feature.icon className="mb-3 h-8 w-8 text-violet-400" />
                                <h4 className="font-semibold text-slate-200">
                                    {feature.title}
                                </h4>
                                <p className="mt-2 text-sm text-slate-400">
                                    {feature.description}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="border-t border-slate-800/50 bg-slate-900/20">
                <div className="mx-auto max-w-5xl px-4 py-20">
                    <h3 className="mb-12 text-center text-2xl font-bold sm:text-3xl">
                        How it works
                    </h3>
                    <div className="grid gap-8 sm:grid-cols-3">
                        <div className="text-center">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-violet-900/50 text-xl font-bold text-violet-300">
                                1
                            </div>
                            <h4 className="font-semibold">Dom Creates Contract</h4>
                            <p className="mt-2 text-sm text-slate-400">
                                Set the opening balance, interest rate, payment schedule, fees, and punishments.
                                Publish to the marketplace or send directly.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-violet-900/50 text-xl font-bold text-violet-300">
                                2
                            </div>
                            <h4 className="font-semibold">Sub Signs & Pays</h4>
                            <p className="mt-2 text-sm text-slate-400">
                                Terms are locked when signed. Make payments, watch interest accrue,
                                and accept fees and taxes as they come.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-violet-900/50 text-xl font-bold text-violet-300">
                                3
                            </div>
                            <h4 className="font-semibold">Everything Tracked</h4>
                            <p className="mt-2 text-sm text-slate-400">
                                Immutable ledger records every transaction. Amortization schedules
                                update in real time. No hiding, no cheating.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-5xl px-4 py-20 text-center">
                <h3 className="text-2xl font-bold sm:text-3xl">
                    Ready to take control?
                </h3>
                <p className="mt-3 text-slate-400">
                    Create your first debt contract in minutes.
                </p>
                <div className="mt-8">
                    <Link to="/register">
                        <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-base px-10">
                            Create Account
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-slate-800/50 bg-slate-950">
                <div className="mx-auto max-w-5xl px-4 py-8 text-center text-xs text-slate-600">
                    <p>DebtBound &mdash; Financial domination contract management</p>
                    <p className="mt-1">All activities are consensual. Safe, sane, and consensual.</p>
                    <div className="mt-3 flex justify-center gap-4">
                        <Link to="/docs" className="hover:text-slate-400 transition-colors">Documentation</Link>
                        <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms of Service</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
